// NPM Imports
import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import Modal from "react-responsive-modal";
import { connect } from "react-redux";
import { Loader } from "@revvsales/components";

// Components Imports
import AddProduct from "./AddProduct";
import AgGridWrapper from "../AgGridWrapper";
import TitleBar from "../TitleBar";
import { ProductsBar } from "../ControlBar";
import { Conditional } from "../../commonComponents";
import { CheckBoxEditor } from "../AgGridCellEditor";
import { CheckBoxRenderer, MultiSelectRenderer } from "../AgGridCellRenderer";

// commonService
import { fetchAppConfig } from "../../actions/CommonActions/commonService";
import { getViewConfig } from "../../actions/ProductActions/productServices";
import { signOutUser } from "../../actions/AuthActions/authActions";

// Actions Imports
import ProductActions from "../../actions/ProductActions";

// Internals Imports
import { GlobalUtils } from "../../utils";
import { GRID, PERMISSIONS, LOADING_MSG } from "../../constants";

// Destructuring
const { getInitData, togglePopUp, updateProductData } = ProductActions;
const { saveGridState, checkUserPermission } = GlobalUtils;
const { GRID_PAGE_TYPE } = GRID;

// Declarations
let gridApi = null;
const gridState = `${GRID_PAGE_TYPE.PRODUCTS}GridState`;

class ProductGrid extends Component {
  static rowData = null;

  state = {
    showLoader: false,
    filteredText: "",
    frameworkComponents: {
      CheckBoxEditor,
      CheckBoxRenderer,
      MultiSelectRenderer,
    },
  };

  componentDidMount() {
    this._toggleLoader(true);
    getViewConfig()
      .then(res => {
        this._onInit(res);
      })
      .catch(err => {
        fetchAppConfig()
          .then(response => {
            console.debug("configSuccess response", response);
            getViewConfig()
              .then(res => {
                this._onInit(res);
              })
              .catch(error => {
                signOutUser();
                console.debug("configSuccess response", error);
              });
          })
          .catch(error => {
            signOutUser();
            console.debug("configFails", error);
          });
        console.debug("res", err);
      });
  }

  _nukePopupData = () => {
    ProductGrid.rowData = null;
  };

  _togglePopUp = flag => {
    this.props.togglePopUp(flag);
    if (!flag) {
      // ProductGrid.rowData = null;
      this._nukePopupData();
    }
  };

  // Callback on ag-grid ready state
  _onGridReady = api => {
    gridApi = api;
    GlobalUtils.saveGridAPi(api);
    // To Store & Retrive the user grid state
    // if (readDataFromLocalStorage(gridState)) {
    //   restoreGridState(gridState);
    // } else {
    //   saveGridState(gridState);
    // }
    // Resize columns after it's completly loaded
    api.api.sizeColumnsToFit();
  };

  // Callback when a grid dispay change is detected
  _saveGridState = () => {
    saveGridState(gridState);
  };

  // Callback when a cell change is detected
  _onCellValueChanged = params => {
    this.props.updateProductData(params.data, true);
  };

  // Set Right Click options
  _getContextMenuItems = params => {
    const contextMenuItems = [
      "autoSizeAll",
      {
        name: "Reset Columns",
        action: () => gridApi.api.sizeColumnsToFit(),
      },
      "copy",
      "export",
    ];

    // edit product row if user has update permission
    if (checkUserPermission(PERMISSIONS.PRODUCTS.UPDATE_PRODUCTS)) {
      contextMenuItems.unshift({
        name: "Edit Row",
        action: () => {
          ProductGrid.rowData = params.node.data;
          this._togglePopUp(true);
        },
      });
    }
    return contextMenuItems;
  };

  _toggleLoader = flag => {
    this.setState({ showLoader: flag });
  };

  _onFilterChange = event => {
    gridApi.api.setQuickFilter(event.target.value);
    this.setState({ filteredText: event.target.value });
  };

  _onInit = res => {
    this._toggleLoader(true);
    this.props.getInitData(res).then(() => {
      this._toggleLoader(false);
    });
  };

  render() {
    return (
      <Fragment>
        <div className="productgrid-container">
          <div className="page-topbar">
            <TitleBar
              iconName="shapes"
              title="Products"
              subTitle="This is the place to manage all your standalone products."
            />
            <div className="create-container" />
          </div>
          <div className="productgrid">
            <Conditional if={!this.state.showLoader}>
              <ProductsBar
                onFilterChange={this._onFilterChange}
                filterTxt={this.state.filteredText}
              />
            </Conditional>
            <Conditional if={this.state.showLoader}>
              <Loader label={LOADING_MSG.PRODUCTS} />;
            </Conditional>
            <Conditional
              if={this.props.rowData.length > 0 && !this.state.showLoader}>
              <div className="productgrid-wrapper">
                <div className="ag-theme-balham productgrid-grid">
                  <AgGridWrapper
                    // Grid Data
                    columnData={this.props.columnDefs}
                    rowData={this.props.rowData}
                    // Grid Callbacks
                    onCellValueChanged={this._onCellValueChanged}
                    onDragStopped={this._saveGridState}
                    onGridReady={this._onGridReady}
                    onSortChanged={this._saveGridState}
                    // Grid Functions
                    animateRows
                    deltaRowDataMode
                    frameworkComponents={this.state.frameworkComponents}
                    getContextMenuItems={this._getContextMenuItems}
                    getRowNodeId={data => data.index}
                    rowHeight={34}
                    suppressDragLeaveHidesColumns
                    suppressPropertyNamesCheck
                  />
                </div>
              </div>
              <div className="productgrid-footer">
                <div className="productgrid-btns">
                  <div className="productgrid-valuewrapper">
                    <div className="productgrid-row-count">
                      {this.props.rowData.length} items
                    </div>
                  </div>
                </div>
              </div>
            </Conditional>
            <Modal
              open={this.props.showPopUp}
              onClose={() => this._togglePopUp(false)}
              closeOnOverlayClick={false}
              center>
              <AddProduct
                data={ProductGrid.rowData}
                nukePopupData={this._nukePopupData}
              />
            </Modal>
          </div>
        </div>
      </Fragment>
    );
  }
}

ProductGrid.defaultProps = {};

ProductGrid.propTypes = {
  // Props
  columnDefs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  rowData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showPopUp: PropTypes.bool.isRequired,

  // Actions
  getInitData: PropTypes.func.isRequired,
  togglePopUp: PropTypes.func.isRequired,
  updateProductData: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  const { columnDefs, rowData, showPopUp } = state.ProductsStore;
  return {
    columnDefs,
    rowData,
    showPopUp,
  };
};

const mapDispatchToProps = {
  getInitData,
  togglePopUp,
  updateProductData,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProductGrid);



// WEBPACK FOOTER //
// ./src/components/Products/ProductGrid.js